/**
 * Single RN-identity predicate for roster gates, coverage dashboards, and DB
 * evidence functions.
 *
 * WHY ONE PREDICATE.
 * Three call sites previously answered "is this an RN?" differently:
 *   - roster gate: role OR classification, with RN / LRN / RNAGY aliases
 *   - DB coverage: `staff.role = 'REGISTERED_NURSE'` only (agency RN = zero)
 *   - runtime panel: substring match on role + required role
 *
 * Medea has ~130 agency RN shifts. Under the DB-only definition they contribute
 * nothing to 24/7 RN coverage. Under the gate they do. One predicate closes
 * that split; SQL mirrors it via `public.is_registered_nurse_identity(...)`.
 *
 * CLINICAL_LEAD is NOT RN for coverage purposes here. A clinical lead may be an
 * RN by registration, but coverage evidence must identify an RN acting as RN —
 * role/classification tokens that mean registered nurse, not leadership title.
 *
 * SINGLE SOURCE OF TRUTH.
 * Exact tokens and prefixes below are the TS authority. Migration
 * `20270936000062` must encode the same set. Drift is caught by
 * `packages/utils/src/__tests__/statutory-screening-floor-sql-parity.test.ts`
 * (RN section).
 */

import { classifyCareMinuteRoleBucket } from './care-minutes';

/** Exact identity tokens that mean registered nurse (after normalisation). */
export const RN_IDENTITY_EXACT_TOKENS = ['RN', 'LRN', 'RNAGY', 'REGISTERED_NURSE'] as const;

/**
 * Prefixes that mean registered nurse (after normalisation).
 * Checked with startsWith; order is not significant.
 */
export const RN_IDENTITY_PREFIXES = ['RN_', 'LRN_', 'RNAGY_', 'LEAD_RN'] as const;

/**
 * Fold free-text role labels into comparable identity tokens.
 *
 * WHY THIS SHAPE.
 * Live roster boards store required_role as "Lead RN", "Lead RN (Senior)", and
 * similar human labels — not only REGISTERED_NURSE. Without the same fold on
 * both sides, TS matches LEAD_RN while SQL compared the raw "LEAD RN" against
 * LEAD_RN% and missed Medea's 36 Lead RN shifts.
 *
 * Steps (must stay lockstep with public.normalise_rn_identity_token in
 * migration 20270936000062):
 *   1. trim + upper
 *   2. expand & → AND
 *   3. every non-alphanumeric run → single underscore (spaces, hyphens,
 *      punctuation, and parenthetical boundaries all collapse)
 *   4. strip leading/trailing underscores
 */
export function normaliseRnIdentityToken(value: string | null | undefined): string {
  return String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/&/g, ' AND ')
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * True when role and/or classification identify a registered nurse.
 * Agency aliases (RNAGY) and leading-RN codes (LRN) count — they are still RNs.
 */
export function isRegisteredNurseIdentity(input: {
  readonly role?: string | null;
  readonly classificationCode?: string | null;
  readonly classificationLevel?: string | null;
  readonly requiredRole?: string | null;
}): boolean {
  const candidates = [
    input.role,
    input.classificationCode,
    input.classificationLevel,
    input.requiredRole,
  ];
  for (const candidate of candidates) {
    if (classifyCareMinuteRoleBucket(candidate) === 'RN') return true;
    const token = normaliseRnIdentityToken(candidate);
    if (!token) continue;
    if ((RN_IDENTITY_EXACT_TOKENS as readonly string[]).includes(token)) return true;
    if ((RN_IDENTITY_PREFIXES as readonly string[]).some((prefix) => token.startsWith(prefix))) {
      return true;
    }
    // Substring form used by free-text required_role values such as
    // "Lead Registered Nurse" after normalisation → LEAD_REGISTERED_NURSE.
    if (token.includes('REGISTERED_NURSE')) return true;
  }
  return false;
}
